<template>
  <div class="overlay" @click.self="$emit('fechar')">
    <div class="modal-filtros">
      <div class="modal-header">
        <span class="modal-label">Filtros Atividades</span>
        <button class="btn-fechar-modal" @click="$emit('fechar')">
          ×
        </button>
      </div>

      <div class="modal-corpo">
        <!-- Unidade da UFC -->
        <div class="coluna">
          <p class="coluna-titulo">Unidade da UFC:</p>
          <FiltroSelect v-model="unidadeSelecionada" :items="unidades" msgTodos="Todas as unidades" />
        </div>

        <!-- Categoria -->
        <div class="coluna">
          <p class="coluna-titulo">Categoria:</p>
          <FiltroSelect v-model="categoriaSelecionada" :items="listaCategorias" msgTodos="Todas as categorias" />
        </div>

        <!-- Tipo -->
        <div class="coluna">
          <p class="coluna-titulo">Tipo:</p>
          <FiltroSelect v-model="tipoSelecionado" :items="listaTipos" msgTodos="Todos os tipos" />
        </div>

        <!-- Modalidade -->
        <div class="coluna">
          <p class="coluna-titulo">Modalidade:</p>
          <FiltroSelect v-model="modalidadeSelecionada" :items="listaModalidades" msgTodos="Todas as modalidades" />
        </div>

        <!-- Status -->
        <div class="coluna">
          <p class="coluna-titulo">Status:</p>
          <FiltroSelect v-model="statusSelecionado" :items="listaStatus" msgTodos="Todos os status" />
        </div>
      </div>

      <div class="modal-rodape">
        <button class="btn-limpar" @click="limparFiltros">
          Limpar filtros
        </button>
        <button class="btn-filtrar" @click="aplicarFiltros">
          Filtrar
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import FiltroSelect from "@/components/common/FiltroSelect.vue";
import atividadeService from "@/service/atividadeService";
import unidadesService from "@/service/unidadesService";
import userInfos from "@/utils/userInfos";
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";

const props = defineProps({
  filtrosIniciais: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["fechar", "aplicarFiltros"]);

const router = useRouter();
const route = useRoute();

const auth = useAuthStore();
const { isAutenticado, estaRenovando } = storeToRefs(auth);
const isLogado = computed(
  () => isAutenticado.value || estaRenovando.value || !!userInfos.getUserInfos()
);

const unidades = ref([]);
const listaTipos = ref([]);

const listaCategorias = computed(() => {
  const opcoes = [
    { id: "ATIVIDADE_LIVRE", nome: "Atividades Livres" },
    { id: "ATIVIDADE_INSCRICAO", nome: "Com Inscrição" },
  ];

  if (isLogado.value) {
    opcoes.unshift({ id: "MINHA_ATIVIDADE", nome: "Minhas Atividades" });
  }

  return opcoes;
});

const listaModalidades = [
  { id: "PRESENCIAL", nome: "Presencial" },
  { id: "REMOTA", nome: "Online" },
  { id: "HIBRIDA", nome: "Híbrida" },
];

const listaStatus = [
  { id: "PROGRAMADA", nome: "Programada" },
  { id: "INSCRICOES_ABERTAS", nome: "Inscrições Abertas" },
  { id: "CANCELADA", nome: "Cancelada" },
  { id: "ENCERRADA", nome: "Encerrada" },
  { id: "EM_EXECUCAO", nome: "Em Execução" },
];

function obterValorInicial(campo) {
  const queryVal =
    campo === "categorias" || campo === "categoria"
      ? route.query.categorias || route.query.categoria
      : route.query[campo];
  if (queryVal) {
    const arr = Array.isArray(queryVal) ? queryVal : String(queryVal).split(",");
    return arr[0] || "";
  }
  const propVal =
    campo === "categorias" || campo === "categoria"
      ? props.filtrosIniciais?.categorias || props.filtrosIniciais?.categoria
      : props.filtrosIniciais?.[campo];
  if (propVal) {
    if (Array.isArray(propVal)) return propVal[0] || "";
    return propVal;
  }
  return "";
}

const unidadeSelecionada = ref(
  route.query.unidadeId || props.filtrosIniciais?.unidadeId || ""
);
const categoriaSelecionada = ref(obterValorInicial("categorias"));
const tipoSelecionado = ref(
  obterValorInicial("tipo") ? Number(obterValorInicial("tipo")) : ""
);
const modalidadeSelecionada = ref(obterValorInicial("modalidade"));
const statusSelecionado = ref(obterValorInicial("status"));

function sincronizarComQuery(q) {
  unidadeSelecionada.value = q.unidadeId || "";
  const cat = q.categorias || q.categoria;
  categoriaSelecionada.value = cat
    ? (Array.isArray(cat) ? cat[0] : String(cat).split(",")[0]) || ""
    : "";
  tipoSelecionado.value = q.tipo ? Number(String(q.tipo).split(",")[0]) : "";
  modalidadeSelecionada.value = q.modalidade ? String(q.modalidade).split(",")[0] : "";
  statusSelecionado.value = q.status ? String(q.status).split(",")[0] : "";
}

watch(
  () => route.query,
  (newQuery) => {
    sincronizarComQuery(newQuery);
  },
  { deep: true }
);

onMounted(async () => {
  try {
    const [resTipos, resUnidades] = await Promise.all([
      atividadeService.getTiposeSubtipos(),
      unidadesService.getAllUnidades(0, 1000),
    ]);
    if (resTipos && resTipos.length > 0) {
      listaTipos.value = resTipos.map((t) => ({
        id: t.id,
        nome: t.nome,
      }));
    }
    if (resUnidades && Array.isArray(resUnidades)) {
      unidades.value = resUnidades;
      unidades.value = unidades.value.map((unidade) => {
        return { ...unidade, label: unidade.nome.toUpperCase() }
      })
    }
  } catch (e) {
    console.error("Erro ao carregar dados em FiltroAtividade:", e);
  }

  sincronizarComQuery(route.query);
});

function limparFiltros() {
  const query = { ...route.query };
  delete query.unidadeId;
  delete query.categorias;
  delete query.categoria;
  delete query.tipo;
  delete query.modalidade;
  delete query.status;
  delete query.page;

  unidadeSelecionada.value = "";
  categoriaSelecionada.value = "";
  tipoSelecionado.value = "";
  modalidadeSelecionada.value = "";
  statusSelecionado.value = "";

  const resultado = {
    categorias: [],
    categoria: [],
    tipo: [],
    modalidade: [],
    status: [],
    unidadeId: null,
  };

  router.push({ query });
  emit("aplicarFiltros", resultado);
  emit("fechar");
}

function aplicarFiltros() {
  const query = { ...route.query };

  if (unidadeSelecionada.value) {
    query.unidadeId = unidadeSelecionada.value;
  } else {
    delete query.unidadeId;
  }

  delete query.categoria;
  if (categoriaSelecionada.value) {
    query.categorias = categoriaSelecionada.value;
  } else {
    delete query.categorias;
  }

  if (tipoSelecionado.value) {
    query.tipo = tipoSelecionado.value;
  } else {
    delete query.tipo;
  }

  if (modalidadeSelecionada.value) {
    query.modalidade = modalidadeSelecionada.value;
  } else {
    delete query.modalidade;
  }

  if (statusSelecionado.value) {
    query.status = statusSelecionado.value;
  } else {
    delete query.status;
  }

  delete query.page;

  const categoriasArray = categoriaSelecionada.value
    ? [categoriaSelecionada.value]
    : [];

  const resultado = {
    categorias: categoriasArray,
    categoria: categoriasArray,
    tipo: tipoSelecionado.value ? [tipoSelecionado.value] : [],
    modalidade: modalidadeSelecionada.value
      ? [modalidadeSelecionada.value]
      : [],
    status: statusSelecionado.value ? [statusSelecionado.value] : [],
    unidadeId: unidadeSelecionada.value || null,
  };

  router.push({ query });
  emit("aplicarFiltros", resultado);
  emit("fechar");
}
</script>

<style scoped>
.modal-filtros {
  border-radius: 12px;
  width: 420px;
  max-width: 1200px;
  overflow: hidden;
}


.btn-fechar {
  position: absolute;
  right: 14px;
  background: transparent;
  border: 2px solid var(--branco);
  color: var(--branco);
  border-radius: 6px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-corpo {
  display: flex;
  flex-direction: column;
  background: var(--branco);
  gap: 16px;
}

.coluna {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.coluna-titulo {
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--preto);
  margin: 0;
}


.btn-limpar {
  border: 2px solid var(--roxo-medio);
  background: var(--branco);
  color: var(--roxo-medio);
  border-radius: 10px;
  padding: 8px 24px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-limpar:hover {
  background: var(--roxo-superficie);
}

.btn-filtrar {
  background: var(--roxo-primario);
  color: var(--branco);
  border: none;
  border-radius: 10px;
  padding: 8px 24px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.btn-filtrar:hover {
  background: var(--roxo-medio);
}
</style>
